import React, { useContext, useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getBgImage, getLogo } from '../../../api/aws/s3';
import { companiesRef } from '../../../api/firebase/firestore/company';
import { getIssueInfiniteScrollfromClient } from '../../../api/firebase/firestore/issue';
import { usersRef } from '../../../api/firebase/firestore/user';
import {
  mypageHeader,
  mypageHeaderTitle,
  sideBar
} from '../../../constants/Location';
import { useIndustry } from '../../../constants/Select';
import { screenType, select } from '../../../constants/enum';
import { AuthContext } from '../../../contexts/auth';
import { throwError } from '../../../modules/error';
import noImage from '../../assets/noImage.svg';
import CustomLoading from '../../uiParts/CustomLoading';
import RegisterInfo from '../../uiParts/RegisterInfo';
import RegisterInfoGivenListSize from '../../uiParts/RegisterInfoGivenListSize';
import Sidebar from '../../uiParts/Sidebar';

import type { Issue } from '../../../api/firebase/firestore/issue/issueType';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';

const styles = {
  mypageHeader,
  mypageHeaderTitle,
  sideBar
};

const ClientRegisterInfo: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('title.client.mypageCompany');
  });

  const { currentUser } = useContext(AuthContext);

  const [userCompany, setUserCompany] = useState({
    bgImgId: '',
    foundationYear: '',
    industry: 0,
    industryOther: '',
    introduction: '',
    logoId: '',
    name: '',
    product: [{ productNames: null, productUrls: null }],
    status: 2,
    url: ''
  });

  const init = async (): Promise<void> => {
    try {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const ownCampanyRef = doc(
            companiesRef,
            userSnap.data()?.companyId as string
          );
          const companySnap = await getDoc(ownCampanyRef);
          const bgImg = await getBgImage(companySnap);
          const logoId = await getLogo(companySnap);
          setUserCompany((prevState) => ({
            ...prevState,
            bgImgId: bgImg,
            foundationYear: companySnap.data()?.foundationYear as string,
            industry: companySnap.data()?.industry as number,
            industryOther: companySnap.data()?.industryOther as string,
            introduction: companySnap.data()?.introduction as string,
            logoId,
            name: companySnap.data()?.name as string,
            product: companySnap.data()?.product as [],
            status: companySnap.data()?.status as number,
            url: companySnap.data()?.url as string
          }));
        }
      }
    } catch (e) {
      throwError(e);
    }
  };

  const [issueList, setIssueList] = useState<Issue[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [showList, setShowList] = React.useState<boolean>(false);

  const appendIssues = (
    snapshot: Array<QueryDocumentSnapshot<DocumentData>>
  ): void => {
    const gotIssues: Issue[] = snapshot.map((document) => {
      const issue = document.data() as Issue;
      return issue;
    });

    setIssueList(issueList.concat(gotIssues));
  };

  const loadMore = async (): Promise<void> => {
    if (currentUser === null) return;

    const { docs, empty } = await getIssueInfiniteScrollfromClient(
      issueList,
      currentUser
    );
    if (empty) {
      setHasMore(false);
      return;
    }
    appendIssues(docs);
  };

  const showIndustry = (i: number): string => {
    const industryList = useIndustry();
    const data = industryList.find((element): boolean => element.value === i);
    if (i === select.other) {
      return userCompany.industryOther;
    }
    return data?.option ?? '';
  };

  const table = {
    aboveDesc: showIndustry(userCompany.industry),
    aboveType: t('common.industryType'),
    bottomDesc: userCompany.product,
    bottomType: t('common.commercialUrl'),
    middleDesc: `${userCompany.foundationYear}${t('common.year')}`,
    middleType: t('common.yearEstablished')
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await init();
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, [currentUser]);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography sx={styles.sideBar} component="div">
        <Sidebar screenType={screenType.client} />
        <Typography component="div" sx={{ flex: 1 }}>
          <Typography sx={styles.mypageHeader} component="div">
            <Typography sx={styles.mypageHeaderTitle}>
              {t('register.registInfo')}
            </Typography>
          </Typography>
          <RegisterInfo
            screen={screenType.client}
            face={userCompany.logoId ?? noImage}
            faceUrl={userCompany.logoId ?? noImage}
            backImage={userCompany.bgImgId ?? noImage}
            companyName={userCompany.name}
            companyUrl={userCompany.url}
            statusNum={userCompany.status}
            desc={userCompany.introduction}
            table={table}
          />
          {showList && (
            <RegisterInfoGivenListSize
              screen={screenType.client}
              issueList={issueList}
              loadMore={loadMore}
              hasMore={hasMore}
            />
          )}
        </Typography>
      </Typography>
    </>
  );
};

export default ClientRegisterInfo;
